/**
 * Agent Error Boundary
 *
 * Catches and handles errors in the agent feature to prevent app crashes.
 * Provides user-friendly error messages and recovery options.
 */

import React from 'react';

class AgentErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console for development
    console.error('🚨 Agent Error Boundary caught an error:', error, errorInfo);

    // Store error details for debugging
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // In production, you might want to log this to an error reporting service
    // Example: ErrorReportingService.captureException(error, { extra: errorInfo });
  }

  handleRetry = () => {
    // Reset error state to retry
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  handleRefresh = () => {
    // Full page refresh as last resort
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { fallback } = this.props;

      // Use custom fallback if provided
      if (fallback) {
        return fallback(this.state.error, this.handleRetry, this.handleRefresh);
      }

      // Default error UI
      return (
        <div className="agent-error-boundary">
          <div className="agent-error-content">
            <div className="agent-error-icon">⚠️</div>
            <h3 className="agent-error-title">Something went wrong</h3>
            <p className="agent-error-message">
              We ran into an unexpected error. Please try again, or refresh the page.
            </p>

            <div className="agent-error-actions">
              <button
                className="button button-primary"
                onClick={this.handleRetry}
              >
                Try Again
              </button>
              <button
                className="button button-secondary"
                onClick={this.handleRefresh}
              >
                Refresh Page
              </button>
            </div>

            {/* Development error details */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="agent-error-details">
                <summary>Error Details (Development)</summary>
                <pre className="agent-error-stack">
                  {this.state.error.toString()}
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AgentErrorBoundary;